import React from 'react';
import { Navigate } from 'react-router-dom';
import { usePermissions } from '../../hooks/usePermissions';
import { FeaturePermission } from '../../constants/featurePermissions';
import LoadingSpinner from '../ui/LoadingSpinner';

interface PermissionRouteRequirement {
  features?: FeaturePermission[];
  requireAll?: boolean;
  minimumLevel?: 'read' | 'write' | 'admin';
  customCheck?: (context: any) => boolean;
}

interface PermissionBasedRouteProps {
  children: React.ReactNode;
  requirement: PermissionRouteRequirement;
  redirectTo?: string;
  fallback?: React.ReactNode;
  showLoading?: boolean;
}

export const PermissionBasedRoute: React.FC<PermissionBasedRouteProps> = ({
  children,
  requirement,
  redirectTo = '/dashboard',
  fallback = null,
  showLoading = true
}) => {
  const { checker, context, loading } = usePermissions();

  if (loading && showLoading) {
    return <LoadingSpinner />;
  }

  // If no checker or context, allow access for authenticated users (fallback during development)
  if (!checker || !context) {
    return <Navigate to="/auth/login" replace />;
  }

  // Check feature permissions first
  if (requirement.features && requirement.features.length > 0) {
    try {
      const result = checker.check({
        features: requirement.features,
        requireAll: requirement.requireAll,
        minimumLevel: requirement.minimumLevel
      });

      if (result.hasAccess) {
        return <>{children}</>;
      }
    } catch (error) {
      console.warn('Permission check failed, falling back to role check:', error);
    }
  }

  // Custom permission check
  if (requirement.customCheck) {
    try {
      if (requirement.customCheck(context)) {
        return <>{children}</>;
      }
    } catch (error) {
      console.warn('Custom permission check failed:', error);
    }
  }


  // Removed development fallback - system now uses feature permissions only

  // Access denied
  if (fallback) {
    return <>{fallback}</>;
  }

  return <Navigate to={redirectTo} replace />;
};

// Convenience components for common patterns
export const AdminRoute: React.FC<{ children: React.ReactNode; redirectTo?: string }> = ({
  children,
  redirectTo = '/dashboard'
}) => (
  <PermissionBasedRoute
    requirement={{
      features: ['admin_read']
    }}
    redirectTo={redirectTo}
  >
    {children}
  </PermissionBasedRoute>
);

export const SuperAdminRoute: React.FC<{ children: React.ReactNode; redirectTo?: string }> = ({
  children,
  redirectTo = '/dashboard'
}) => (
  <PermissionBasedRoute
    requirement={{
      customCheck: (context) => context.userRole === 'super_admin'
    }}
    redirectTo={redirectTo}
  >
    {children}
  </PermissionBasedRoute>
);

export const TenantRoute: React.FC<{ children: React.ReactNode; redirectTo?: string }> = ({
  children,
  redirectTo = '/auth/login'
}) => (
  <PermissionBasedRoute
    requirement={{
      customCheck: (context) => context.userRole === 'tenant'
    }}
    redirectTo={redirectTo}
  >
    {children}
  </PermissionBasedRoute>
);

export const FeatureRoute: React.FC<{
  children: React.ReactNode;
  feature: FeaturePermission;
  level?: 'read' | 'write';
  redirectTo?: string;
}> = ({ children, feature, level = 'read', redirectTo = '/dashboard' }) => {
  const actualFeature = level === 'write'
    ? (feature.replace('_read', '_write') as FeaturePermission)
    : feature;

  return (
    <PermissionBasedRoute
      requirement={{
        features: [actualFeature]
      }}
      redirectTo={redirectTo}
    >
      {children}
    </PermissionBasedRoute>
  );
};

export const MultiFeatureRoute: React.FC<{
  children: React.ReactNode;
  features: FeaturePermission[];
  requireAll?: boolean;
  redirectTo?: string;
}> = ({ children, features, requireAll = false, redirectTo = '/dashboard' }) => (
  <PermissionBasedRoute
    requirement={{
      features,
      requireAll
    }}
    redirectTo={redirectTo}
  >
    {children}
  </PermissionBasedRoute>
);

export default PermissionBasedRoute;